import { apiClient, downloadFile } from './client';

/**
 * Custom reports, on a session or an event (`scope` is 'session' | 'event').
 * Each page has its own title, prompt, selected sessions and generated output.
 */
const path = (scope, scopeId, suffix = '') => `/api/reports/custom-reports/${scope}/${scopeId}${suffix}`;
const langParams = (lang) => (lang ? { lang } : undefined);

export const customReportApi = {
  list: async (scope, scopeId) => {
    const response = await apiClient.get(path(scope, scopeId));
    return response.data;
  },

  create: async (scope, scopeId, options = {}) => {
    const response = await apiClient.post(path(scope, scopeId), {
      title: options.title,
      prompt: options.prompt,
      session_ids: options.session_ids,
      report_kind: options.report_kind,
      day_number: options.day_number,
      country: options.country,
      take_official: options.take_official,
      mode: options.mode,
    });
    return response.data;
  },

  get: async (scope, scopeId, pageId, lang = null) => {
    const response = await apiClient.get(path(scope, scopeId, `/${pageId}`), { params: langParams(lang) });
    return response.data;
  },

  update: async (scope, scopeId, pageId, options = {}) => {
    const response = await apiClient.patch(path(scope, scopeId, `/${pageId}`), {
      title: options.title,
      prompt: options.prompt,
      session_ids: options.session_ids,
      report_kind: options.report_kind,
      day_number: options.day_number,
      country: options.country,
      take_official: options.take_official,
    });
    return response.data;
  },

  remove: async (scope, scopeId, pageId) => {
    await apiClient.delete(path(scope, scopeId, `/${pageId}`));
  },

  generate: async (scope, scopeId, pageId, options = {}) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/generate`), {
      title: options.title,
      prompt: options.prompt,
      session_ids: options.session_ids,
    });
    return response.data;
  },

  setOfficial: async (scope, scopeId, pageId) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/set-official`));
    return response.data;
  },

  /** Save hand-edited output. Without `lang` the original is edited, otherwise that translation. */
  updateContent: async (scope, scopeId, pageId, { content, sessionIndex = null, lang = null } = {}) => {
    const response = await apiClient.put(
      path(scope, scopeId, `/${pageId}/content`),
      { content, session_index: sessionIndex },
      { params: langParams(lang) }
    );
    return response.data;
  },

  sendToMissions: async (scope, scopeId, pageId, lang = null) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/send-missions`), null, {
      params: langParams(lang),
    });
    return response.data;
  },

  publishToDigwatch: async (scope, scopeId, pageId, lang = null) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/send-digwatch`), null, {
      params: langParams(lang),
    });
    return response.data;
  },

  /** Create a DigWatch newsletter draft; `dryRun` only validates the payload. */
  createNewsletter: async (scope, scopeId, pageId, lang = null, dryRun = false) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/newsletter`), null, {
      params: { ...(lang ? { lang } : {}), ...(dryRun ? { dry_run: true } : {}) },
    });
    return response.data;
  },

  /** Report language plus every translation and its status. */
  getLanguages: async (scope, scopeId, pageId) => {
    const response = await apiClient.get(path(scope, scopeId, `/${pageId}/languages`));
    return response.data;
  },

  translate: async (scope, scopeId, pageId, lang, modelType = null) => {
    const response = await apiClient.post(path(scope, scopeId, `/${pageId}/translate`), null, {
      params: { lang, ...(modelType ? { model_type: modelType } : {}) },
    });
    return response.data;
  },

  deleteTranslation: async (scope, scopeId, pageId, lang) => {
    await apiClient.delete(path(scope, scopeId, `/${pageId}/translations/${lang}`));
  },

  downloadJson: async (scope, scopeId, pageId, fallbackName, lang = null) => {
    await downloadFile(path(scope, scopeId, `/${pageId}/json`), {
      params: langParams(lang),
      fallbackName: fallbackName || `custom_report_${pageId}.json`,
      mimeType: 'application/json',
    });
  },

  downloadPdf: async (scope, scopeId, pageId, fallbackName, lang = null) => {
    await downloadFile(path(scope, scopeId, `/${pageId}/pdf`), {
      params: langParams(lang),
      fallbackName: fallbackName || `custom_report_${pageId}.pdf`,
      mimeType: 'application/pdf',
    });
  },
};
